import React, { useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BRAND, whatsappLink } from "@/lib/config";
import { track } from "@/lib/analytics";
import { PRIVACY } from "@/lib/privacyContent";
import { ArrowRight, Loader2, MessageCircle, Globe, Instagram, Mail, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    preferred_name: "",
    email: "",
    phone: "",
    level: "",
    interest: "",
    message: "",
    website: "", // honeypot
  });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t.contact.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, language: lang });
      const firstName =
        (form.preferred_name || "").trim() ||
        (form.name || "").trim().split(/\s+/)[0];
      const successMsg =
        firstName && t.contact.successWithName
          ? t.contact.successWithName.replace("{name}", firstName)
          : t.contact.success;
      toast.success(successMsg);
      track("lead_submit_success", {
        language: lang,
        level: form.level || null,
        interest: form.interest || null,
        has_preferred_name: Boolean(form.preferred_name?.trim()),
        has_phone: Boolean(form.phone?.trim()),
        has_message: Boolean(form.message?.trim()),
      });
      setForm({ name: "", preferred_name: "", email: "", phone: "", level: "", interest: "", message: "", website: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
      track("lead_submit_error", { language: lang });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#f4f1ea] py-24 sm:py-32 border-t border-[#e5e3db]"
    >
      <span id="aula-gratis" className="block -mt-24 pt-24" aria-hidden="true" />
      <span id="primeira-aula-gratis" className="block -mt-24" aria-hidden="true" />
      <span id="contato-leadgen" className="block -mt-24" aria-hidden="true" />
      <span id="solicitar-chamada" className="block -mt-24" aria-hidden="true" />
      <span id="cotacao" className="block -mt-24" aria-hidden="true" />
      <span id="delf-dalf-tef-tcf" className="block -mt-24" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <h2 className="overline">{t.contact.seoH2}</h2>
          <p
            data-testid="contact-title"
            className="font-display mt-4 text-4xl sm:text-5xl tracking-tight text-[#1e2a5a]"
          >
            {t.contact.title}
          </p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5c5c5c] max-w-md">
            {t.contact.subtitle}
          </p>

          <ul className="mt-10 flex flex-col gap-6 text-sm">
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 mt-1 shrink-0 text-[#d9381e]" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1">
                  E-mail
                </p>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="contact-email-link"
                  className="text-[#1e2a5a] link-underline break-all"
                >
                  {BRAND.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle className="h-4 w-4 mt-1 shrink-0 text-[#25d366]" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1">
                  WhatsApp
                </p>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="text-[#1e2a5a] link-underline"
                >
                  {BRAND.whatsappDisplay}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Globe className="h-4 w-4 mt-1 shrink-0 text-[#d9381e]" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1">
                  Site
                </p>
                <a
                  href={BRAND.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-website-link"
                  className="text-[#1e2a5a] link-underline"
                >
                  www.{BRAND.websiteDisplay.replace(/^www\./, "")}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Instagram className="h-4 w-4 mt-1 shrink-0 text-[#d9381e]" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1">
                  Instagram
                </p>
                <a
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="text-[#1e2a5a] link-underline"
                >
                  {BRAND.instagramHandle}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3 mt-2 pt-6 border-t border-[#1e2a5a]/10">
              <BookOpen className="h-4 w-4 mt-1 shrink-0 text-[#d9381e]" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1">
                  Blog gratuito
                </p>
                <a
                  href="https://www.solteseufrances.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-blog-link"
                  className="text-[#1e2a5a] link-underline font-medium"
                >
                  www.solteseufrances.com.br
                </a>
                <p className="mt-2 text-xs text-[#5c5c5c] leading-relaxed">
                  Muitas informações, exercícios, músicas, cultura francesa e muito mais.
                </p>
              </div>
            </li>
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <Field label={t.contact.labels.name} htmlFor="lead-name" required>
            <Input
              id="lead-name"
              data-testid="lead-name-input"
              required
              value={form.name}
              onChange={update("name")}
              className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
            />
          </Field>
          <Field label={t.contact.labels.preferredName} htmlFor="lead-preferred-name">
            <Input
              id="lead-preferred-name"
              data-testid="lead-preferred-name-input"
              value={form.preferred_name}
              onChange={update("preferred_name")}
              maxLength={80}
              className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
            />
          </Field>
          <Field label={t.contact.labels.email} htmlFor="lead-email" required>
            <Input
              id="lead-email"
              type="email"
              data-testid="lead-email-input"
              required
              value={form.email}
              onChange={update("email")}
              className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
            />
          </Field>
          <Field label={t.contact.labels.phone} htmlFor="lead-phone">
            <Input
              id="lead-phone"
              data-testid="lead-phone-input"
              value={form.phone}
              onChange={update("phone")}
              className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
            />
          </Field>
          <Field label={t.contact.labels.level} htmlFor="lead-level">
            <Select value={form.level} onValueChange={(v) => update("level")(v)}>
              <SelectTrigger
                id="lead-level"
                data-testid="lead-level-select"
                className="bg-white border-[#e5e3db] h-11"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#e5e3db]">
                {t.contact.levels.map((lvl) => (
                  <SelectItem key={lvl} value={lvl}>
                    {lvl}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t.contact.labels.interest} htmlFor="lead-interest" colSpan>
            <Select value={form.interest} onValueChange={(v) => update("interest")(v)}>
              <SelectTrigger
                id="lead-interest"
                data-testid="lead-interest-select"
                className="bg-white border-[#e5e3db] h-11"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#e5e3db]">
                {t.contact.interests.map((it) => (
                  <SelectItem key={it} value={it}>
                    {it}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t.contact.labels.message} htmlFor="lead-message" colSpan>
            <Textarea
              id="lead-message"
              data-testid="lead-message-input"
              rows={4}
              value={form.message}
              onChange={update("message")}
              className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20"
            />
          </Field>

          {/* Honeypot — hidden from humans, bots tend to fill every input */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={update("website")}
            style={{
              position: "absolute",
              left: "-10000px",
              width: "1px",
              height: "1px",
              opacity: 0,
              pointerEvents: "none",
            }}
          />

          <div className="sm:col-span-2 pt-2">
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="group inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.contact.submitting}
                </>
              ) : (
                <>
                  {t.contact.submit}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p
              data-testid="contact-consent-micro"
              className="mt-4 text-xs leading-relaxed text-[#5c5c5c] max-w-xl"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  (PRIVACY[lang] || PRIVACY.pt).consentMicro,
                  { ALLOWED_TAGS: ["a", "strong", "em", "b", "i", "br", "span"], ALLOWED_ATTR: ["href", "target", "rel", "class"] },
                ),
              }}
            />
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children, colSpan, required }) => (
  <label
    htmlFor={htmlFor}
    className={`flex flex-col gap-2 ${colSpan ? "sm:col-span-2" : ""}`}
  >
    <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
      {label}
      {required ? <span className="text-[#d9381e]"> *</span> : null}
    </span>
    {children}
  </label>
);

export default Contact;
